import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Radio, MapPin, Signal, Globe, Percent, Users } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';

interface RadioSectionProps {
  timeRange: string;
}

const RadioSection: React.FC<RadioSectionProps> = ({ timeRange }) => {
  // Generate radio data based on time range
  const spinsData = generateSpinsData(timeRange);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card className="bg-card/95 border-border/50">
        <CardHeader>
          <CardTitle className="text-xl flex items-center gap-2 text-white/95">
            <Radio className="h-5 w-5 text-soundcharts-purple" />
            Radio Airplay
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={spinsData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis dataKey="name" stroke="rgba(255,255,255,0.7)" fontSize={12} />
                <YAxis stroke="rgba(255,255,255,0.7)" fontSize={12} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'rgba(0,0,0,0.8)', 
                    borderColor: 'rgba(255,255,255,0.1)',
                    borderRadius: '8px',
                    color: 'rgba(255,255,255,0.9)'
                  }} 
                />
                <Bar dataKey="spins" fill="#9b87f5" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
      
      <Card className="bg-card/95 border-border/50">
        <CardHeader>
          <CardTitle className="text-xl flex items-center gap-2 text-white/95">
            <Globe className="h-5 w-5 text-soundcharts-purple" />
            Radio Coverage
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="border border-soundcharts-purple/30 bg-black/20 rounded-lg p-4">
              <div className="text-sm font-medium mb-4 flex items-center justify-between text-white/95">
                <span>Top Radio Stations</span>
                <Signal className="h-4 w-4 text-soundcharts-purple" />
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between text-white/95">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-soundcharts-purple/30 flex items-center justify-center text-xs">1</div>
                    <span>KEXP 90.3 FM</span>
                  </div>
                  <span className="text-sm">42 spins</span>
                </div>
                <div className="flex items-center justify-between text-white/95">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-soundcharts-purple/30 flex items-center justify-center text-xs">2</div>
                    <span>WFUV 90.7 FM</span>
                  </div>
                  <span className="text-sm">38 spins</span>
                </div>
                <div className="flex items-center justify-between text-white/95">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-soundcharts-purple/30 flex items-center justify-center text-xs">3</div>
                    <span>XMU Radio</span>
                  </div>
                  <span className="text-sm">26 spins</span>
                </div>
                <div className="flex items-center justify-between text-white/95">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-soundcharts-purple/30 flex items-center justify-center text-xs">4</div>
                    <span>WXPN 88.5 FM</span>
                  </div>
                  <span className="text-sm">19 spins</span>
                </div>
              </div>
            </div>
            
            <div className="border border-soundcharts-purple/30 bg-black/20 rounded-lg p-4">
              <div className="text-sm font-medium mb-4 flex items-center justify-between text-white/95">
                <span>Top Regions</span>
                <MapPin className="h-4 w-4 text-soundcharts-purple" />
              </div>
              <div className="space-y-3">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-white/95">Los Angeles</span>
                    <span className="text-xs text-white/95">28%</span>
                  </div>
                  <div className="h-2 rounded-full bg-black/30 overflow-hidden">
                    <div className="h-full bg-soundcharts-purple" style={{ width: '28%' }}></div>
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-white/95">New York</span>
                    <span className="text-xs text-white/95">22%</span>
                  </div>
                  <div className="h-2 rounded-full bg-black/30 overflow-hidden">
                    <div className="h-full bg-soundcharts-purple" style={{ width: '22%' }}></div>
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-white/95">Seattle</span>
                    <span className="text-xs text-white/95">18%</span>
                  </div>
                  <div className="h-2 rounded-full bg-black/30 overflow-hidden">
                    <div className="h-full bg-soundcharts-purple" style={{ width: '18%' }}></div>
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-white/95">Chicago</span>
                    <span className="text-xs text-white/95">14%</span>
                  </div>
                  <div className="h-2 rounded-full bg-black/30 overflow-hidden">
                    <div className="h-full bg-soundcharts-purple" style={{ width: '14%' }}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Card className="bg-card/95 border-border/50 lg:col-span-2">
        <CardHeader>
          <CardTitle className="text-xl flex items-center gap-2 text-white/95">
            <Signal className="h-5 w-5 text-soundcharts-purple" />
            Radio Performance Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="border border-soundcharts-purple/30 bg-black/20 rounded-md p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-white/70">Total Spins</span>
                <Radio className="h-4 w-4 text-soundcharts-purple" />
              </div>
              <div className="text-xl font-semibold text-white/95">248</div>
              <div className="flex items-center gap-1 text-xs mt-1 text-green-500">
                <Percent className="h-3 w-3" />
                <span>+22% from last {timeRange}</span>
              </div>
            </div>
            
            <div className="border border-soundcharts-purple/30 bg-black/20 rounded-md p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-white/70">Radio Stations</span>
                <Signal className="h-4 w-4 text-soundcharts-purple" />
              </div>
              <div className="text-xl font-semibold text-white/95">42</div>
              <div className="flex items-center gap-1 text-xs mt-1 text-green-500">
                <Percent className="h-3 w-3" />
                <span>+8 new this {timeRange}</span>
              </div>
            </div>
            
            <div className="border border-soundcharts-purple/30 bg-black/20 rounded-md p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-white/70">Audience Reach</span>
                <Users className="h-4 w-4 text-soundcharts-purple" />
              </div>
              <div className="text-xl font-semibold text-white/95">2.4M</div>
              <div className="flex items-center gap-1 text-xs mt-1 text-green-500">
                <Percent className="h-3 w-3" />
                <span>+15% from last {timeRange}</span>
              </div>
            </div>
            
            <div className="border border-soundcharts-purple/30 bg-black/20 rounded-md p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-white/70">Markets</span>
                <Globe className="h-4 w-4 text-soundcharts-purple" />
              </div>
              <div className="text-xl font-semibold text-white/95">18</div>
              <div className="flex items-center gap-1 text-xs mt-1 text-green-500">
                <Percent className="h-3 w-3" />
                <span>+4 new this {timeRange}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Helper function to generate mock data
const generateSpinsData = (timeRange: string) => {
  let dataPoints = [];
  const baseSpins = 20;
  
  switch (timeRange) {
    case 'week':
      dataPoints = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, index) => ({
        name: day,
        spins: baseSpins + Math.floor(Math.random() * 15) + (index * 2)
      }));
      break;
    case 'month':
      dataPoints = ['Week 1', 'Week 2', 'Week 3', 'Week 4'].map((week, index) => ({
        name: week,
        spins: baseSpins + Math.floor(Math.random() * 20) + (index * 8)
      }));
      break;
    case 'year':
      dataPoints = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((month, index) => ({
        name: month,
        spins: baseSpins + Math.floor(Math.random() * 30) + (index * 5)
      }));
      break;
    default:
      dataPoints = ['2020', '2021', '2022', '2023', '2024'].map((year, index) => ({
        name: year,
        spins: baseSpins + Math.floor(Math.random() * 50) + (index * 20)
      }));
  }
  
  return dataPoints;
};

export default RadioSection;
